import React from 'react';

import autoBind from 'react-autobind';
import Switch, { Case } from 'react-switch-case';
import FileSaver from "file-saver";

// import other components within the same project here

import Localization from "../../Localization.jsx"

import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import { Button, Icon, Divider } from '@material-ui/core';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';

// keep in sync with user_batch_create() in the API project
const MAX_BATCH_ROW = 500;
const MAX_FILE_SIZE = 1024 * 1024; // 1 MB

const REQUIRED_HEADERS = ["username", "password", "school_id"];
const OPTIONAL_HEADERS = ["display_name", "email", "role"];

const STEP_UPLOAD = 0;
const STEP_CONFIRM = 1;
const STEP_RESULT = 2;

const styles = {
  sectionTitle: {
    fontWeight: 'bold',
    padding: '14px 0 6px 0',
  },
  hintText: {
    color: 'rgba(0, 0, 0, 0.54)',
    fontSize: '0.85em',
    padding: '4px 0',
  },
  errorText: {
    color: '#f44336',
    padding: '4px 0',
  },
  okText: {
    color: '#2e7d32',
    padding: '4px 0',
  },
  tableBox: {
    maxHeight: '320px',
    overflowY: 'auto',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    margin: '8px 0',
  },
};

class BatchCreateUserDialog extends React.Component {
  constructor(props) {
    super(props);
    this.refs = React.createRef();

    // state variables here
    this.state = {
      open: false,
      step: STEP_UPLOAD,

      fileName: "",
      parseError: "",
      ignoredColumns: [],
      csvUsers: [],       // rows parsed from the CSV, sent to the server for validation / insert

      validating: false,
      creating: false,

      // per row results returned by the server
      serverRows: [],
      totalCount: 0,
      validCount: 0,
      invalidCount: 0,
      createdCount: 0,
    };
    autoBind(this);
  }

  show() {
    this.setState({
      open: true,
      step: STEP_UPLOAD,
      fileName: "",
      parseError: "",
      ignoredColumns: [],
      csvUsers: [],
      validating: false,
      creating: false,
      serverRows: [],
      totalCount: 0,
      validCount: 0,
      invalidCount: 0,
      createdCount: 0,
    });
    this.state.open = true;
    this.state.step = STEP_UPLOAD;
    this.state.fileName = "";
    this.state.parseError = "";
    this.state.ignoredColumns = [];
    this.state.csvUsers = [];
    this.state.validating = false;
    this.state.creating = false;
    this.state.serverRows = [];
    this.state.totalCount = 0;
    this.state.validCount = 0;
    this.state.invalidCount = 0;
    this.state.createdCount = 0;
  }

  handleClose() {
    // no accidental close while the real insert is running
    if (this.state.creating) {
      return;
    }
    this.setState({ open: false });
    this.state.open = false;
  }

  downloadTemplateClicked() {
    let headers = REQUIRED_HEADERS.concat(OPTIONAL_HEADERS).join(",");
    // BOM so Excel opens the file as UTF-8
    let blob = new Blob(["\uFEFF" + headers + "\r\n"], { type: "text/csv;charset=utf-8" });
    FileSaver.saveAs(blob, "batch_create_user_template.csv");
  }

  selectFileClicked() {
    if (this.fileInput) {
      // allow choosing the same file again after fixing it
      this.fileInput.value = null;
      this.fileInput.click();
    }
  }

  fileSelected(e) {
    let file = (e.target.files && e.target.files.length > 0) ? e.target.files[0] : null;
    if (!file) {
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      this.setParseError(file.name, "File is too large (max 1 MB)");
      return;
    }

    let reader = new FileReader();
    reader.onload = (ev) => {
      this.parseCSVFile(file.name, ev.target.result);
    };
    reader.onerror = () => {
      this.setParseError(file.name, "Cannot read the file");
    };
    reader.readAsText(file, "UTF-8");
  }

  setParseError(fileName, message) {
    this.setState({
      fileName: fileName,
      parseError: message,
      ignoredColumns: [],
      csvUsers: [],
    });
    this.state.fileName = fileName;
    this.state.parseError = message;
    this.state.ignoredColumns = [];
    this.state.csvUsers = [];
  }

  // RFC 4180 style CSV parser, handles quoted cells, escaped quotes ("")
  // and commas / line breaks inside quotes, CRLF / LF / CR line endings.
  // Returns [{line: <1-based line number>, cells: [..]}], blank lines removed
  // but line numbers still match the original file.
  parseCSVText(text) {
    // strip BOM
    if (text.length > 0 && text.charCodeAt(0) === 0xFEFF) {
      text = text.slice(1);
    }

    let rows = [];
    let cells = [];
    let cell = "";
    let inQuotes = false;
    let line = 1;          // current line in the original file
    let rowStartLine = 1;  // line where the current row started

    let pushCell = () => { cells.push(cell); cell = ""; };
    let pushRow = () => { pushCell(); rows.push({ line: rowStartLine, cells: cells }); cells = []; };

    for (let i = 0; i < text.length; i++) {
      let c = text[i];

      // line breaks are counted even inside quoted cells, so the reported
      // row numbers always match the original file
      if (c === '\r' || c === '\n') {
        if (c === '\r' && text[i + 1] === '\n') {
          i++;
        }
        line++;
        if (inQuotes) {
          cell += '\n';
        } else {
          pushRow();
          rowStartLine = line;
        }
        continue;
      }

      if (inQuotes) {
        if (c === '"') {
          if (text[i + 1] === '"') {
            cell += '"';
            i++;
          } else {
            inQuotes = false;
          }
        } else {
          cell += c;
        }
      } else {
        if (c === '"') {
          inQuotes = true;
        } else if (c === ',') {
          pushCell();
        } else {
          cell += c;
        }
      }
    }
    if (cell !== "" || cells.length > 0) {
      pushRow();
    }

    // drop rows where every cell is blank
    return rows.filter((r) => r.cells.some((c) => String(c).trim() !== ""));
  }

  parseCSVFile(fileName, text) {
    let rows = this.parseCSVText(text);

    if (rows.length === 0) {
      this.setParseError(fileName, "The file is empty");
      return;
    }

    // first non blank row is the header row
    let headerRow = rows[0];
    let normalize = (h) => String(h).trim().toLowerCase().replace(/\s+/g, "_");
    let headers = headerRow.cells.map(normalize);

    let knownHeaders = REQUIRED_HEADERS.concat(OPTIONAL_HEADERS);
    let columnIndex = {}; // header name => column position
    let ignoredColumns = [];
    let duplicated = [];

    headers.forEach((h, idx) => {
      if (h === "") {
        return;
      }
      if (knownHeaders.indexOf(h) >= 0) {
        if (columnIndex[h] !== undefined) {
          duplicated.push(h);
        } else {
          columnIndex[h] = idx;
        }
      } else {
        ignoredColumns.push(headerRow.cells[idx]);
      }
    });

    if (duplicated.length > 0) {
      this.setParseError(fileName, "Duplicated column(s) in the header row: " + duplicated.join(", "));
      return;
    }

    let missing = REQUIRED_HEADERS.filter((h) => columnIndex[h] === undefined);
    if (missing.length > 0) {
      this.setParseError(fileName, "Missing required column(s): " + missing.join(", ") + ". The first row must be the header row, please use the CSV template.");
      return;
    }

    let dataRows = rows.slice(1);
    if (dataRows.length === 0) {
      this.setParseError(fileName, "No data rows found below the header row");
      return;
    }
    if (dataRows.length > MAX_BATCH_ROW) {
      this.setParseError(fileName, "Too many rows (" + dataRows.length + "), max " + MAX_BATCH_ROW + " users per batch");
      return;
    }

    // rows with extra data beyond the declared columns are usually a broken
    // file (unquoted comma etc), refuse them instead of silently dropping data
    let overflowLines = [];
    dataRows.forEach((r) => {
      for (let i = headers.length; i < r.cells.length; i++) {
        if (String(r.cells[i]).trim() !== "") {
          overflowLines.push(r.line);
          break;
        }
      }
    });
    if (overflowLines.length > 0) {
      let shown = overflowLines.slice(0, 5).join(", ");
      let more = (overflowLines.length > 5) ? " and " + (overflowLines.length - 5) + " more" : "";
      this.setParseError(fileName, "Row " + shown + more + " has more columns than the header row. Please fix the file and upload again.");
      return;
    }

    let cellAt = (r, name) => {
      let idx = columnIndex[name];
      if (idx === undefined || idx >= r.cells.length) {
        return "";
      }
      return String(r.cells[idx]).trim();
    };

    let csvUsers = dataRows.map((r) => {
      return {
        row: r.line,
        username: cellAt(r, "username"),
        password: cellAt(r, "password"),
        school_id: cellAt(r, "school_id"),
        display_name: cellAt(r, "display_name"),
        email: cellAt(r, "email"),
        role: cellAt(r, "role"),
      };
    });

    this.setState({
      fileName: fileName,
      parseError: "",
      ignoredColumns: ignoredColumns,
      csvUsers: csvUsers,
    });
    this.state.fileName = fileName;
    this.state.parseError = "";
    this.state.ignoredColumns = ignoredColumns;
    this.state.csvUsers = csvUsers;

    this.runServerValidation();
  }

  // dry run on the server: username / school checks need the DB
  runServerValidation() {
    this.setState({ validating: true });
    this.state.validating = true;

    let requestObj = {
      validate_only: 1,
      users: this.state.csvUsers,
    };
    this.props.root.refs.API.request("user_batch_create", requestObj, this.validateOK, this.validateFailed);
  }

  validateOK(res) {
    this.setState({ validating: false });
    this.state.validating = false;

    if (!res || !res.body || !res.body.result || !res.body.result.rows) {
      this.props.root.addNotification("Batch create user validation failed, please make sure you are logged in as admin");
      return;
    }

    let result = res.body.result;
    this.setState({
      step: STEP_CONFIRM,
      serverRows: result.rows,
      totalCount: result.total,
      validCount: result.valid_count,
      invalidCount: result.invalid_count,
    });
    this.state.step = STEP_CONFIRM;
    this.state.serverRows = result.rows;
    this.state.totalCount = result.total;
    this.state.validCount = result.valid_count;
    this.state.invalidCount = result.invalid_count;
  }

  validateFailed(err) {
    console.log("batch create user validation failed " + err);
    this.setState({ validating: false });
    this.state.validating = false;
    this.props.root.addNotification("Batch create user validation failed");
  }

  // the real insert, only after the admin confirmed the summary
  confirmCreateClicked() {
    this.setState({ creating: true });
    this.state.creating = true;

    let requestObj = {
      validate_only: 0,
      users: this.state.csvUsers,
    };
    this.props.root.refs.API.request("user_batch_create", requestObj, this.createOK, this.createFailed);
  }

  createOK(res) {
    this.setState({ creating: false });
    this.state.creating = false;

    if (!res || !res.body || !res.body.result || !res.body.result.rows) {
      this.props.root.addNotification("Batch create user failed, please make sure you are logged in as admin");
      return;
    }

    let result = res.body.result;
    this.setState({
      step: STEP_RESULT,
      serverRows: result.rows,
      totalCount: result.total,
      validCount: result.valid_count,
      invalidCount: result.invalid_count,
      createdCount: result.created_count,
    });
    this.state.step = STEP_RESULT;
    this.state.serverRows = result.rows;
    this.state.totalCount = result.total;
    this.state.validCount = result.valid_count;
    this.state.invalidCount = result.invalid_count;
    this.state.createdCount = result.created_count;

    this.props.root.addNotification(Localization.strings.CREATED + ": " + result.created_count);

    // refresh the user list tab of the admin dialog
    if (this.props.parent && this.props.parent.refreshUserList) {
      this.props.parent.refreshUserList();
    }
  }

  createFailed(err) {
    console.log("batch create user failed " + err);
    this.setState({ creating: false });
    this.state.creating = false;
    this.props.root.addNotification("Batch create user failed");
  }

  backToUploadClicked() {
    this.setState({
      step: STEP_UPLOAD,
      fileName: "",
      parseError: "",
      ignoredColumns: [],
      csvUsers: [],
      serverRows: [],
      totalCount: 0,
      validCount: 0,
      invalidCount: 0,
    });
    this.state.step = STEP_UPLOAD;
    this.state.fileName = "";
    this.state.parseError = "";
    this.state.ignoredColumns = [];
    this.state.csvUsers = [];
    this.state.serverRows = [];
    this.state.totalCount = 0;
    this.state.validCount = 0;
    this.state.invalidCount = 0;
  }

  roleName(role) {
    return (parseInt(role) === 1) ? "Teacher" : "Student";
  }

  fieldReferenceTable() {
    let rows = [
      ["username", "Yes", "Must be unique, max 50 characters"],
      ["password", "Yes", "6 - 30 characters, letters and digits only, must contain both"],
      ["school_id", "Yes", "Numeric school ID, see the School List tab"],
      ["display_name", "No", "Defaults to the username when blank"],
      ["email", "No", "Checked for valid format when provided"],
      ["role", "No", "0 or student = Student, 1 or teacher = Teacher. Default: Student"],
    ];
    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Column</TableCell>
            <TableCell>Required</TableCell>
            <TableCell>Notes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {
            rows.map((row, i) => (
              <TableRow key={i}>
                <TableCell>{row[0]}</TableCell>
                <TableCell>{row[1]}</TableCell>
                <TableCell>{row[2]}</TableCell>
              </TableRow>
            ))
          }
        </TableBody>
      </Table>
    );
  }

  uploadStep() {
    return (
      <div>
        <div style={styles.sectionTitle}>1. Download the CSV template</div>
        <div style={styles.hintText}>
          The first row must be the header row. Keep the headers and fill one user per row.
          If display names contain Chinese characters, save the file as "CSV UTF-8".
        </div>
        <Button
          color="primary"
          variant="outlined"
          onClick={this.downloadTemplateClicked}
        >
          <Icon style={{ marginRight: '6px' }}>get_app</Icon>
          {Localization.strings.DOWNLOAD_CSV_TEMPLATE}
        </Button>

        <div style={styles.sectionTitle}>2. Fill in the users</div>
        {this.fieldReferenceTable()}
        <div style={styles.hintText}>
          Max {MAX_BATCH_ROW} users per file. Empty rows are ignored. Nothing is created before you confirm the summary in the next step.
        </div>

        <div style={styles.sectionTitle}>3. Upload the CSV file</div>
        <Button
          color="primary"
          variant="contained"
          disabled={this.state.validating}
          onClick={this.selectFileClicked}
        >
          <Icon style={{ marginRight: '6px' }}>publish</Icon>
          {this.state.validating ? "Validating..." : Localization.strings.UPLOAD_FILE}
        </Button>
        <input
          type="file"
          accept=".csv,text/csv"
          style={{ display: 'none' }}
          ref={(el) => { this.fileInput = el; }}
          onChange={this.fileSelected}
        />

        {
          this.state.parseError !== "" &&
          <div style={styles.errorText}>
            {this.state.fileName}: {this.state.parseError}
          </div>
        }
      </div>
    );
  }

  confirmStep() {
    return (
      <div>
        <div>
          {this.state.fileName}: {this.state.totalCount} data row(s) found.
        </div>
        {
          this.state.ignoredColumns.length > 0 &&
          <div style={styles.hintText}>
            Ignored unknown column(s): {this.state.ignoredColumns.join(", ")}
          </div>
        }
        <div style={styles.okText}>
          {this.state.validCount} user(s) will be created.
        </div>
        {
          this.state.invalidCount > 0 &&
          <div style={styles.errorText}>
            {this.state.invalidCount} row(s) have errors and will be skipped.
          </div>
        }
        <Divider style={{ margin: '8px 0' }} />
        {this.resultTable(false)}
        <div style={styles.hintText}>
          Please check the list above. No user has been created yet, click "Create {this.state.validCount} User(s)" to confirm.
        </div>
      </div>
    );
  }

  resultStep() {
    let failedRows = this.state.serverRows.filter((row) => row['status'] !== "created");
    return (
      <div>
        <div style={styles.okText}>
          {this.state.createdCount} user(s) created.
        </div>
        {
          failedRows.length > 0 &&
          <div style={styles.errorText}>
            {failedRows.length} row(s) were not created, see the list below.
          </div>
        }
        <Divider style={{ margin: '8px 0' }} />
        {this.resultTable(true)}
      </div>
    );
  }

  resultTable(isResultStep) {
    return (
      <div style={styles.tableBox}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Row</TableCell>
              <TableCell>Username</TableCell>
              <TableCell>School ID</TableCell>
              <TableCell>Display Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {
              this.state.serverRows.map((row, i) => {
                let statusCell = null;
                if (row['status'] === "ok") {
                  statusCell = <span style={styles.okText}>OK</span>;
                } else if (row['status'] === "created") {
                  statusCell = <span style={styles.okText}>{Localization.strings.CREATED}</span>;
                } else {
                  statusCell = <span style={styles.errorText}>{row['message']}</span>;
                }
                return (
                  <TableRow key={i}>
                    <TableCell>{row['row']}</TableCell>
                    <TableCell>{row['username']}</TableCell>
                    <TableCell>{row['school_id']}</TableCell>
                    <TableCell>{row['display_name']}</TableCell>
                    <TableCell>{row['email']}</TableCell>
                    <TableCell>{this.roleName(row['role'])}</TableCell>
                    <TableCell>{statusCell}</TableCell>
                  </TableRow>
                );
              })
            }
          </TableBody>
        </Table>
      </div>
    );
  }

  render() {
    return (
      <div>
        <Dialog
          open={this.state.open}
          fullWidth={true}
          maxWidth={'md'}
          onClose={this.handleClose}
        >
          <DialogTitle>
            {Localization.strings.BATCH_CREATE_USER}
          </DialogTitle>

          <DialogContent>
            <Switch condition={this.state.step}>
              <Case value={STEP_UPLOAD}>
                {this.uploadStep()}
              </Case>
              <Case value={STEP_CONFIRM}>
                {this.confirmStep()}
              </Case>
              <Case value={STEP_RESULT}>
                {this.resultStep()}
              </Case>
            </Switch>
          </DialogContent>

          <DialogActions style={{ padding: '16px 24px' }}>
            <Switch condition={this.state.step}>
              <Case value={STEP_UPLOAD}>
                <Button onClick={this.handleClose}>
                  {Localization.strings.CANCEL}
                </Button>
              </Case>
              <Case value={STEP_CONFIRM}>
                <Button
                  disabled={this.state.creating}
                  onClick={this.backToUploadClicked}
                >
                  {Localization.strings.BACK}
                </Button>
                <Button
                  disabled={this.state.creating}
                  onClick={this.handleClose}
                >
                  {Localization.strings.CANCEL}
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  disabled={this.state.validCount <= 0 || this.state.creating}
                  onClick={this.confirmCreateClicked}
                >
                  {this.state.creating ? "Creating..." : "Create " + this.state.validCount + " User(s)"}
                </Button>
              </Case>
              <Case value={STEP_RESULT}>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={this.handleClose}
                >
                  {Localization.strings.DONE}
                </Button>
              </Case>
            </Switch>
          </DialogActions>
        </Dialog>
      </div>
    )
  }
}

export default BatchCreateUserDialog;
